import type { ChatClient, ChatPerson, ChatRoomSummary } from '@wtfalch/chat';
import { Button, Callout, Icon, Panes, Pill, Segmented, SegmentedOption } from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';
import { ColleagueHours, type ColleagueHoursSource } from '../hours/ColleagueHours.tsx';
import { Avatar } from '../ui/Avatar.tsx';
import { PersonIdentity } from '../ui/people.tsx';
import { fileType, guestUntilLabel, shortName } from './format.ts';
import { PersonHeader } from './PersonHeader.tsx';
import { directPeerId, StatusLabel } from './presence.tsx';
import type { ChatAttachment, ChatTimelineEvent } from './types.ts';

/** `roomFiles`' own page size (agora#B5): enough for one screenful before a
 *  reader has to ask for older ones. */
const FILES_PAGE_SIZE = 30;

const OPEN_FILE_FAILED_MESSAGE = "Couldn't open this file. Try again.";
const FILES_FAILED_MESSAGE = "Couldn't load the files shared here. Try again.";

/** A room and a shared panel, plus the Pinned tab the header pin opens
 *  (#255). Exported so `Chat.tsx`, which owns which tab is open (the header
 *  pin has to force it to `'pinned'` even while the panel is already open
 *  on something else), can name it too. */
export type InfoPanelTab = 'room' | 'shared' | 'pinned' | 'threads';

const UNPIN_FAILED_MESSAGE = "Couldn't remove this pin. Try again.";

/**
 * The room's own info, in the third column when no thread is open --
 * matching the canvas's `dm_side()`, cut down to what `ChatClient` actually
 * has: who is in the room (`roomMembers`), its files (`roomFiles`, on the
 * room's own timeline, not any thread's -- a thread's files are its own
 * conversation), and, since ticket #8 landed, what is pinned. No meetings,
 * no links, no search -- see the campaign ledger's Findings.
 *
 * Shown for a channel as well as a direct message: `roomMembers` and
 * attachments both mean exactly the same thing for either, and the board
 * draws this panel only for a DM because that is the state its mock
 * happened to capture, not because a channel has nothing to show here.
 *
 * **Shared tab.** `roomFiles` walks Synapse's own history rather than only
 * what this client has already loaded into the timeline, so it shows a
 * room's files back to their start, not just its own loaded window. A new
 * file arriving resets the list to the first page rather than being spliced
 * in -- simpler than reconciling an in-progress "Load older files" walk
 * against a live insert, and the common case anyway is opening this tab
 * fresh. "Load older files" appends with `roomFiles`' own cursor
 * (`{ files, next }`, agora#B5) until `next` is `null`.
 *
 * **Pinned tab (#255).** `pinnedMessages` is a plain synchronous read, same
 * as `roomMembers` -- re-read on the one `onTimelineChanged` subscription
 * this panel already keeps, rather than a second listener. Unpinning can
 * still be refused (a room whose power levels predate ticket #8), so it
 * fails into a `Callout` here exactly as pinning does in `Room.tsx`'s own
 * header.
 *
 * **`tab` is controlled, but does not have to be.** `Chat.tsx` owns it so
 * the header's pin button (`onOpenPinned`) can force this panel open on
 * `'pinned'` even while it is already open showing something else --
 * plain local state could not do that from outside. Left out, this panel
 * keeps the tab itself, which is what every test below still does.
 */
export function InfoPanel({
  client,
  room,
  onClose,
  tab: tabProp,
  onTabChange,
  hours,
  onOpenThread,
}: {
  client: ChatClient;
  room: ChatRoomSummary;
  onClose: () => void;
  tab?: InfoPanelTab;
  onTabChange?: (tab: InfoPanelTab) => void;
  /** Where a member's working hours are read, when this company has them
   *  (agora#258). Absent: the member rows show a name only. */
  hours?: ColleagueHoursSource;
  /** Opens a thread by its root message; absent, the Threads tab only lists. */
  onOpenThread?: (rootId: string) => void;
}) {
  const [internalTab, setInternalTab] = useState<InfoPanelTab>('room');
  const tab = tabProp ?? internalTab;
  const setTab = onTabChange ?? setInternalTab;
  const [members, setMembers] = useState<ChatPerson[]>(() => client.roomMembers(room.id));
  const [pinned, setPinned] = useState<readonly ChatTimelineEvent[]>(() =>
    client.pinnedMessages(room.id),
  );
  const [pinError, setPinError] = useState<string | null>(null);
  const [files, setFiles] = useState<readonly ChatAttachment[]>([]);
  const [filesNext, setFilesNext] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [filesFailed, setFilesFailed] = useState(false);
  // Every object URL a file open has made -- kept alive past the click (a
  // new tab still has to read it) rather than revoked right after, and
  // revoked all together only once this panel goes away.
  const openedFileUrls = useRef<Set<string>>(new Set());

  useEffect(
    () => () => {
      for (const url of openedFileUrls.current) URL.revokeObjectURL(url);
      openedFileUrls.current.clear();
    },
    [],
  );

  useEffect(() => {
    let live = true;
    function loadFirstPage() {
      client.roomFiles(room.id, { limit: FILES_PAGE_SIZE }).then(
        (page) => {
          if (!live) return;
          setFilesFailed(false);
          setFiles(page.files);
          setFilesNext(page.next);
        },
        () => {
          if (live) setFilesFailed(true);
        },
      );
    }
    function readRoomState() {
      setMembers(client.roomMembers(room.id));
      setPinned(client.pinnedMessages(room.id));
    }
    readRoomState();
    loadFirstPage();
    const unsubscribe = client.onTimelineChanged(room.id, () => {
      readRoomState();
      loadFirstPage();
    });
    return () => {
      live = false;
      unsubscribe();
    };
  }, [client, room.id]);

  const roomEvents = client.timeline(room.id, null).events;
  const loaded = new Map(roomEvents.map((e) => [e.id, e]));
  // Only the stretch of history this client has loaded: the SDK has no
  // "list threads" call, so an older thread appears once its root is scrolled to.
  const threads = roomEvents.filter((e) => e.thread !== null);
  const peerId = directPeerId(client, room);

  function unpin(eventId: string) {
    client.unpinMessage(room.id, eventId).then(
      () => setPinError(null),
      () => setPinError(UNPIN_FAILED_MESSAGE),
    );
  }

  function loadMoreFiles() {
    if (!filesNext || loadingMore) return;
    setLoadingMore(true);
    client
      .roomFiles(room.id, { limit: FILES_PAGE_SIZE, before: filesNext })
      .then((page) => {
        setFiles((current) => [...current, ...page.files]);
        setFilesNext(page.next);
      })
      .catch(() => setFileError(FILES_FAILED_MESSAGE))
      .finally(() => setLoadingMore(false));
  }

  /** Same call `Stream.tsx`'s `AttachmentBody` fetches a message's own
   *  attachment through -- `loadMedia` resolves `null` rather than
   *  rejecting on any failure (offline, a stale token, the proxy refusing
   *  the room or event), so that alone is the error case here.
   *
   *  Always a download, never `window.open`: a sender picks their own
   *  `mimeType`, so a room file can claim to be `text/html` or
   *  `image/svg+xml` -- either would run as a script in Agora's own
   *  origin if a click ever opened it as a `blob:` URL in a tab, and the
   *  desktop shell (`apps/desktop/security.mjs`) denies `window.open`
   *  outright besides. A download never executes anything. */
  function openFile(attachment: ChatAttachment) {
    client.loadMedia(attachment.mxcUri, room.id, attachment.eventId).then(
      (url) => {
        if (!url) {
          setFileError(OPEN_FILE_FAILED_MESSAGE);
          return;
        }
        openedFileUrls.current.add(url);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = attachment.name;
        anchor.click();
      },
      () => setFileError(OPEN_FILE_FAILED_MESSAGE),
    );
  }

  return (
    <Panes.Column width={360} className="chat-third">
      <aside className="flex flex-1 flex-col min-h-0">
        <div className="flex items-center gap-3 chat-third-head">
          <span className="flex flex-col grow min-w-0">
            <span className="text-sm font-strong truncate">
              {room.isDirect ? room.name : `#${room.name}`}
            </span>
            <span className="text-xs text-muted truncate">Room details</span>
          </span>
          <Button kind="ghost" size="sm" iconOnly aria-label="Hide this" onPress={onClose}>
            <Icon name="close" size={18} />
          </Button>
        </div>
        <div className="chat-third-tabs">
          <Segmented
            label="Info panel view"
            size="sm"
            value={tab}
            onChange={(value) => setTab(value as InfoPanelTab)}
          >
            <SegmentedOption value="room">Room</SegmentedOption>
            <SegmentedOption value="shared">Shared</SegmentedOption>
            <SegmentedOption value="pinned">Pinned</SegmentedOption>
            <SegmentedOption value="threads">Threads</SegmentedOption>
          </Segmented>
        </div>
        <div className="flex flex-col flex-1 overflow-y-auto min-h-0 chat-third-body">
          {tab === 'room' ? (
            <>
              {peerId && <PersonHeader client={client} room={room} userId={peerId} hours={hours} />}
              <div className="navgrp">
                {members.length} member{members.length === 1 ? '' : 's'}
              </div>
              {members.map((m) => (
                <div key={m.userId} className="flex items-center gap-3 py-2 px-4 chat-info-row">
                  <span className="text-sm truncate grow min-w-0">
                    <PersonIdentity
                      name={shortName(m.name)}
                      address={m.userId}
                      chatUserId={m.userId}
                      size="sm"
                    />
                  </span>
                  {m.guest && <Pill tone="info">Guest · until {guestUntilLabel(m.guest)}</Pill>}
                  <StatusLabel client={client} userId={m.userId} />
                  {hours && <ColleagueHours source={hours} chatUserId={m.userId} />}
                </div>
              ))}
            </>
          ) : tab === 'threads' ? (
            <>
              {threads.length === 0 ? (
                <p className="text-xs text-muted chat-third-note">No threads in this room yet.</p>
              ) : (
                <div className="navgrp">
                  {threads.length} thread{threads.length === 1 ? '' : 's'}
                </div>
              )}
              {threads.map((root) => {
                const replies = root.thread?.replyCount ?? 0;
                return (
                  <button
                    key={root.id}
                    type="button"
                    className="chat-file-row chat-thread-row"
                    disabled={!onOpenThread}
                    onClick={() => onOpenThread?.(root.id)}
                  >
                    <Icon name="reply" size={17} />
                    <span className="flex flex-col grow min-w-0">
                      <span className="text-sm truncate">
                        {root.deleted ? 'Message deleted' : root.body}
                      </span>
                      <span className="text-xs text-muted truncate">
                        {shortName(root.senderName)} · {replies}{' '}
                        {replies === 1 ? 'reply' : 'replies'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </>
          ) : tab === 'pinned' ? (
            <>
              {pinError && (
                <Callout tone="bad" timed onDismiss={() => setPinError(null)}>
                  {pinError}
                </Callout>
              )}
              {pinned.length === 0 ? (
                <p className="text-xs text-muted chat-third-note">
                  Nothing is pinned in this room yet.
                </p>
              ) : (
                <div className="navgrp">
                  {pinned.length} pinned message{pinned.length === 1 ? '' : 's'}
                </div>
              )}
              {pinned.map((event) => (
                <div key={event.id} className="flex items-center gap-3 py-2 px-4 chat-info-row">
                  <Avatar name={shortName(event.senderName)} seed={event.senderId} size="sm" />
                  <span className="text-sm truncate grow min-w-0">
                    {event.deleted ? 'Message deleted' : event.body}
                  </span>
                  <Button
                    kind="ghost"
                    size="sm"
                    iconOnly
                    aria-label="Unpin this message"
                    onPress={() => unpin(event.id)}
                  >
                    <Icon name="close" size={15} />
                  </Button>
                </div>
              ))}
            </>
          ) : (
            <>
              {fileError && (
                <Callout tone="bad" timed onDismiss={() => setFileError(null)}>
                  {fileError}
                </Callout>
              )}
              {files.length > 0 && <div className="navgrp">Files shared here</div>}
              {files.length === 0 && filesFailed && (
                <p className="text-xs text-bad chat-third-note" role="alert">
                  {FILES_FAILED_MESSAGE}
                </p>
              )}
              {files.length === 0 && !filesFailed && (
                <p className="text-xs text-muted chat-third-note">
                  Nothing shared yet in this room.
                </p>
              )}
              {files.map((attachment) => {
                // `roomFiles` carries no sender or date, so these come from
                // the part of the room's history already loaded.
                const posted = loaded.get(attachment.eventId);
                return (
                  <button
                    key={attachment.eventId}
                    type="button"
                    className="chat-file-row"
                    onClick={() => openFile(attachment)}
                  >
                    <Icon name={attachment.isImage ? 'image' : 'file'} size={17} />
                    <span className="flex flex-col grow min-w-0">
                      <span className="text-sm truncate">{attachment.name}</span>
                      {posted && (
                        <span className="text-xs text-muted truncate">
                          {shortName(posted.senderName)} ·{' '}
                          {new Date(posted.ts).toLocaleDateString(undefined, {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      )}
                    </span>
                    <Pill quiet>{fileType(attachment.name, attachment.mimeType)}</Pill>
                  </button>
                );
              })}
              {filesNext && (
                <Button kind="ghost" size="sm" onPress={loadMoreFiles} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load older files'}
                </Button>
              )}
            </>
          )}
        </div>
      </aside>
    </Panes.Column>
  );
}
